/* eslint-disable no-continue */
/* eslint-disable no-loop-func */
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import $a from 'axios'
import SparkMD5 from 'spark-md5'
import request from '../../utils/request'

import './index.less'

const axios = $a.create()

const CDN_HOST = 'https://zhiyi-image.oss-cn-hangzhou.aliyuncs.com/'
const CDN_DIR = 'ins-selected/'
const GET_TOKEN_API = '/upload/get-oss-upload-token'

export const UPLOAD_ERROR_TYPE = { TYPE_ERROR: 1, SIZE_ERROR: 2, UPLOAD_ERROR: 3, COUNT_ERROR: 4 }
function unionid({ mime, customDir }) {
  return `${customDir || CDN_DIR + new Date().getTime()}_${Math.random().toString(32).substr(2, 10)}${mime || '.png'}`
}
const ACCEPT_FILE_TYPES = ['image/jpeg', 'image/pjpeg', 'image/png', 'image/x-png']

function validFileType(file) {
  return ACCEPT_FILE_TYPES.includes(file.type)
}
function validFileSize(file, maxSize) {
  return file.size < maxSize * 1000000
}

export const uploadToOSS = async (file, customDir, onUploadProgress, onSuccess, onError) => {
  const spark = new SparkMD5()
  spark.append(file)
  const hashMD5 = spark.end()
  const result = await request.get(GET_TOKEN_API, { params: { dir: customDir || CDN_DIR } })
  const res = result.sign
  const key = unionid({ customDir })
  try {
    await request.formdata(res.host, {
      OSSAccessKeyId: res.accessid,
      signature: res.signature,
      policy: res.policy,
      success_action_status: 200,
      key,
      file
    }, e => onUploadProgress?.(e, key, file))
    const url = CDN_HOST + key
    const { ImageWidth, ImageHeight, FileSize } = (await axios.get(`${url}?x-oss-process=image/info`)).data
    onSuccess({
      url,
      filename: file.name,
      dir: file?.webkitRelativePath,
      width: ImageWidth.value,
      height: ImageHeight.value,
      fileSize: FileSize.value,
      hashMD5,
    })
  } catch (error) {
    onError?.(UPLOAD_ERROR_TYPE.UPLOAD_ERROR, file)
  }
}

export default forwardRef((props, ref) => {
  const { onError, maxSize = 10, customDir, onUploadProgress,
    onUploadedFile, beforeUpload, afterUpload, directory, multiple, children, maxCount = 100, style, onClick, disabled } = props
  const id = new Date().getTime() + Math.random()
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0)
  const $input = useRef()
  const hostFiles = useRef([])
  const originalFiles = useRef([])
  const interrupt = useRef(false)
  useEffect(() => {
    if (directory) {
      $input.current.webkitdirectory = 'webkitdirectory'
      $input.current.directory = 'directory'
    }
  }, [directory])

  useImperativeHandle(ref, () => ({
    interrupt: () => interrupt.current = true
  }))

  const init = () => {
    hostFiles.current = []
    originalFiles.current = []
    $input.current.value = ''
    interrupt.current = false
  }

  useEffect(() => {
    if (originalFiles.current.length && currentUploadIndex >= originalFiles.current.length) {
      afterUpload?.(hostFiles.current, originalFiles.current)
      init()
    }
  }, [currentUploadIndex])

  const onChange = async (e) => {
    delete e.target.files.length
    onMoveChange(e.target.files)
  }

  const onMoveChange = async (file) => {
    setCurrentUploadIndex(0)
    const files = Object.values(file)
    originalFiles.current = files
    if (files.length > maxCount) {
      onError?.(UPLOAD_ERROR_TYPE.COUNT_ERROR)
      init()
      return
    }
    const flag = await beforeUpload?.(originalFiles.current)
    if (flag === false) {
      afterUpload?.(hostFiles.current, originalFiles.current)
      init()
      return
    }
    upload(originalFiles.current)
  }

  const upload = async (files) => {
    for (const file of files) {
      if (!validFileType(file)) {
        onError?.(UPLOAD_ERROR_TYPE.TYPE_ERROR, file)
        setCurrentUploadIndex(pre => pre + 1)
        continue
      }
      if (!validFileSize(file, maxSize)) {
        onError?.(UPLOAD_ERROR_TYPE.SIZE_ERROR, file)
        setCurrentUploadIndex(pre => pre + 1)
        continue
      }
      if (interrupt.current) {
        setCurrentUploadIndex(originalFiles.current.length)
        continue
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadToOSS(
          file,
          customDir,
          onUploadProgress,
          async (data) => {
            onUploadedFile?.()
            hostFiles.current.push(data)
            setCurrentUploadIndex(pre => pre + 1)
          },
          onError
        )
      } catch (e) {
        onError?.(UPLOAD_ERROR_TYPE.UPLOAD_ERROR, file)
      }
    }
  }

  const onDragEnd = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const onDragOver = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const onDrop = (e) => {
    e.stopPropagation()
    e.preventDefault()
    onMoveChange(e.dataTransfer.files)
  }

  return <div
    className="meinian-upload"
    style={style}
    onDragEnd={onDragEnd}
    onDrop={onDrop}
    onDragOver={onDragOver}
    onClick={onClick}
  >
    <label className="input-button" htmlFor={id}>
      {children || '上传'}
    </label>
    <input
      disabled={disabled}
      type="file"
      id={id}
      accept=".jpg,.jpeg,.png"
      className="meinian-input-file"
      ref={$input}
      onChange={onChange}
      multiple={multiple}
    />
  </div>
})
